import { Frame } from "../types";
import { calculateTotalDuration } from "./durations";

export interface LabelTiming {
  label: string;
  startPercent: number;
  endPercent: number;
}

export function buildFrameTimings(
  frameCount: number,
  holdStart: number,
  holdEnd: number,
  duration: number,
  advanceDelay: number,
): LabelTiming[] {
  if (!Number.isFinite(frameCount) || frameCount <= 0) return [];

  const startHold = Math.max(0, Math.min(holdStart ?? 0, 100));
  const endHold = Math.max(0, Math.min(holdEnd ?? 0, 100 - startHold));
  const startHoldMs = (startHold / 100) * duration;
  const endHoldMs = (endHold / 100) * duration;
  const animatedDuration = Math.max(0, duration - startHoldMs - endHoldMs);
  const stepTime = frameCount > 1 ? animatedDuration / (frameCount - 1) : 0;
  const pause = Math.max(0, advanceDelay);
  const totalDuration = calculateTotalDuration(
    duration,
    advanceDelay,
    frameCount,
  );

  const transitionStart = (index: number) => {
    if (index === 0) return 0;
    const offset = startHoldMs + pause * index + stepTime * (index - 1);
    return Math.min(totalDuration, Math.max(0, offset));
  };

  return Array.from({ length: frameCount }, (_, index) => {
    const start =
      index === 0 ? 0 : (transitionStart(index) / totalDuration) * 100;
    const end =
      index === frameCount - 1
        ? 100
        : (transitionStart(index + 1) / totalDuration) * 100;
    return { label: "", startPercent: start, endPercent: end };
  });
}

export function buildFrameLabelTimings(
  frames: Frame[],
  holdStart: number,
  holdEnd: number,
  duration: number,
  advanceDelay: number,
): LabelTiming[] {
  const labels = frames.map((frame) =>
    typeof frame.label === "string" ? frame.label.trim() : "",
  );
  if (!labels.some(Boolean)) return [];

  const timings = buildFrameTimings(
    labels.length,
    holdStart,
    holdEnd,
    duration,
    advanceDelay,
  );
  return timings.map((timing, index) => ({
    ...timing,
    label: labels[index] || "",
  }));
}

export function adjustLabelTimingsForIntro(
  labelTimings: LabelTiming[],
  introPercent: number,
) {
  if (
    !Array.isArray(labelTimings) ||
    labelTimings.length === 0 ||
    introPercent <= 0
  ) {
    return labelTimings;
  }

  const scale = (value: number) =>
    introPercent + (100 - introPercent) * (value / 100);
  return labelTimings.map((timing, index) => {
    const startPercent = index === 0 ? 0 : scale(timing.startPercent);
    const endPercent = scale(timing.endPercent);
    return { ...timing, startPercent, endPercent };
  });
}

export function buildFrameLabelCSS(
  baseName: string,
  labelTimings: LabelTiming[],
) {
  return labelTimings
    .map((timing, index) => {
      const { startPercent, endPercent } = timing;
      const isLast = index === labelTimings.length - 1;
      const initialOpacity = index === 0 ? 1 : 0;

      return (
        `@keyframes ${baseName}-label-${index} {\n` +
        `  0% { opacity: ${initialOpacity}; }\n` +
        `  ${startPercent.toFixed(2)}% { opacity: 1; }\n` +
        `  ${endPercent.toFixed(2)}% { opacity: ${isLast ? 1 : 0}; }\n` +
        `  100% { opacity: ${isLast ? 1 : 0}; }\n` +
        `}`
      );
    })
    .join("\n");
}
